import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import isEqual from 'lodash/isEqual';
import getObjectProperty from 'lodash/get';
import { Loader, StreamSlider, ProductCard } from '../';
import { getApiStatus, getApiError, ApiStatuses, Queries } from '../../api';
import { graphql } from 'react-apollo';
import { withAnalytics } from '../../analytics';
import { Formatters } from '../../utils';
import { SearchResultsPlaceholder } from '../';
import { assetsNoResults } from '../../images';
import StyledCompareResults from './CompareResults.style';

const dataList = {
  maximumBph: {
    title: (
      <FormattedMessage
        id="comparedata.capacity_max"
        description="Capacity (max) BPH label"
        defaultMessage="Capacity (max)"
      />
    ),
    format: num => `${Formatters.toCommaNumber(num)} BPH`
  },

  maximumWorkingHeight: {
    title: (
      <FormattedMessage
        id="comparedata.height_raised"
        description="Height Raised label"
        defaultMessage="Height Raised"
      />
    ),
    format: num => Formatters.toFootInch(num)
  },

  minimumHorsePower: {
    title: (
      <FormattedMessage
        id="comparedata.horsepower_min"
        description="Horsepower (min) label"
        defaultMessage="Horsepower (min)"
      />
    ),
    format: num => Formatters.toCommaNumber(num)
  }
};

const initNumber = 3;
class CompareResults extends PureComponent {
  state = { firstFetch: true };

  numberOfProducts = initNumber;

  renderProductCards() {
    const { products, upsells } = this.props;
    const productLen = getObjectProperty(products, 'length');
    const upsellLen = getObjectProperty(upsells, 'length');

    this.numberOfProducts = productLen < initNumber ? productLen : initNumber;
    // if there're upsells AND products are more than 3
    if (upsellLen > 0 && productLen >= initNumber) {
      this.numberOfProducts = initNumber - 1;
    }

    return products
      .slice(0, this.numberOfProducts)
      .map(product => (
        <ProductCard
          product={product}
          key={`product-${product.id}`}
          isUpsell={false}
          dataList={dataList}
          priceList={Formatters.MSRPPrice}
        />
      ));
  }

  renderUpsellCards() {
    const { upsells } = this.props;
    return upsells
      .slice(0, 1)
      .map(upsell => (
        <ProductCard
          product={upsell}
          key={`upsell-${upsell.id}`}
          isUpsell={true}
          dataList={dataList}
          priceList={Formatters.MSRPPrice}
        />
      ));
  }

  renderError = () => <SearchResultsPlaceholder error />;

  renderWarning = () => (
    <SearchResultsPlaceholder
      images={[assetsNoResults]}
      header={
        <FormattedMessage
          id="subtitle.no_results"
          defaultMessage="No Results"
        />
      }
    >
      <p>
        <FormattedMessage
          id="error.no_match"
          defaultMessage="We couldn't find any matches."
        />
        <br />
        <FormattedMessage
          id="error.adjust_options"
          defaultMessage="Please adjust your selections and try again."
        />
      </p>
    </SearchResultsPlaceholder>
  );

  renderProducts = () => (
    <StyledCompareResults>
      <StreamSlider theme="stretch">
        {this.renderProductCards()}
        {this.renderUpsellCards()}
      </StreamSlider>
    </StyledCompareResults>
  );

  renderBasedOnStatus() {
    const { products, apiStatus } = this.props;
    if (apiStatus === ApiStatuses.Loading) {
      return <Loader active />;
    } else if (apiStatus === ApiStatuses.Error) {
      return this.renderError();
    } else if (products && products.length) {
      return this.renderProducts();
    } else {
      return this.renderWarning();
    }
  }

  render() {
    return this.renderBasedOnStatus();
  }

  trackResultsAnalytics(isRefetch, productIDs) {
    const {
      upsells,
      products,
      analytics,
      apiStatus,
      queryOptions
    } = this.props;
    const tool = 'Comparison Tool';

    analytics.trackQueryEvent(
      {
        Tool: tool,
        isResubmission: isRefetch,
        'Matching products': productIDs.join(', '),
        'Num of Upsell Prod Returned':
          getObjectProperty(upsells, 'length') || 0,
        'Num of Matching Prod Returned':
          getObjectProperty(products, 'length') || 0
      },
      apiStatus,
      queryOptions
    );
    analytics.incrementUserProperty(`Num of ${tool} Completed`);
  }

  // trigger query only when the query options are changed
  componentDidUpdate(prevProps) {
    const {
      queryOptions,
      refetch,
      products,
      updateResults,
      upsells,
      apiStatus
    } = this.props;
    const slicedProducts =
      products && products.concat(upsells).slice(0, this.numberOfProducts);

    const productIDs = (slicedProducts || []).map(product => product.id);

    // if fetched products are changed, trigger updateResults for the parent
    if (!isEqual(prevProps.products, products)) {
      updateResults(productIDs);
    }

    // if queryOptions exists, trigger first fetch analytics
    if (queryOptions && this.state.firstFetch) {
      this.trackResultsAnalytics(false, productIDs);
      this.setState({ firstFetch: false });

      // if queryOptions are updated, refetch() = `isResubmission`, exclude Loading state (results are not final)
    } else if (
      !isEqual(prevProps.queryOptions, queryOptions) &&
      apiStatus === ApiStatuses.Loaded
    ) {
      refetch();
      this.trackResultsAnalytics(true, productIDs);

      // all other cases, if results are changed, trigger
      // beause the previously `Loading` state ones, could `Loaded` now, but `queryOptions` the same, which falls back here
    } else if (!isEqual(prevProps.products, products)) {
      this.trackResultsAnalytics(true, productIDs);
    }
  }
}

CompareResults.propTypes = {
  products: PropTypes.array,
  apiStatus: ApiStatuses.propType,
  error: PropTypes.string,
  analytics: PropTypes.object.isRequired,
  queryOptions: PropTypes.array,
  refetch: PropTypes.func,
  upsells: PropTypes.array
};

export default withAnalytics(
  graphql(Queries.productComparisons, {
    options: props => ({
      variables: { options: props.queryOptions },
      fetchPolicy: 'network-only' // don't cache, we need to get up-to-date results
    }),
    props: props => ({
      ...props,
      products: getObjectProperty(
        props,
        'data.getProductComparisons.products',
        undefined
      ),
      upsells: getObjectProperty(
        props,
        'data.getProductComparisons.upsells',
        undefined
      ),
      apiStatus: getApiStatus(props),
      error: getApiError(props),
      refetch: props.data && props.data.refetch
    })
  })(CompareResults)
);
